/**
 * Social Scheduler Component
 * Manage social media posts across multiple platforms via Metricool integration.
 * Available only for Ultra tier users.
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Calendar,
  Plus,
  Send,
  Clock,
  Loader2,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
  Link2,
  Trash2,
  Edit3,
  MoreVertical,
  Sparkles,
  RefreshCw,
  Instagram,
  Facebook,
  Linkedin,
  Twitter,
  Youtube,
  X,
  ExternalLink,
  Rocket,
  Lock,
  Users,
  BarChart3,
  Settings,
  Eye,
  MessageSquare,
} from "lucide-react";
import { socialSchedulerService } from "../services/SocialSchedulerService";

// Platform icons mapping
const PLATFORM_ICONS = {
  instagram: Instagram,
  facebook: Facebook,
  linkedin: Linkedin,
  twitter: Twitter,
  tiktok: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
    </svg>
  ),
  youtube: Youtube,
  pinterest: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
      <path d="M12 0a12 12 0 00-4.37 23.17c-.1-.94-.2-2.4.04-3.43.22-.94 1.5-6.32 1.5-6.32s-.37-.77-.37-1.9c0-1.78 1.04-3.1 2.33-3.1 1.1 0 1.63.82 1.63 1.8 0 1.1-.7 2.75-1.06 4.28-.3 1.28.64 2.31 1.9 2.31 2.28 0 4.03-2.4 4.03-5.87 0-3.07-2.2-5.22-5.35-5.22-3.65 0-5.78 2.73-5.78 5.56 0 1.1.42 2.28.95 2.92.1.13.12.24.08.38l-.35 1.44c-.06.24-.2.29-.44.17-1.64-.76-2.66-3.14-2.66-5.06 0-4.12 3-7.9 8.64-7.9 4.54 0 8.07 3.24 8.07 7.56 0 4.5-2.84 8.13-6.78 8.13-1.32 0-2.57-.69-3-1.5l-.82 3.1c-.3 1.14-1.1 2.57-1.63 3.44A12 12 0 1012 0z"/>
    </svg>
  ),
  threads: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
      <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.028-3.579.879-6.435 2.525-8.486C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.59 12c.025 3.086.718 5.496 2.057 7.164 1.432 1.781 3.632 2.695 6.54 2.717 1.986-.013 3.758-.477 5.268-1.382 1.78-1.067 2.668-2.497 2.716-4.373.025-.98-.163-1.756-.559-2.308-.387-.54-.965-.878-1.717-.1.006-.33.007-.66.006-.996l-.001-.31c-.025-1.563-.188-2.666-.485-3.278-.355-.732-.999-1.073-1.969-.1.043-.945-.245-1.631-.817-2.042-.607-.435-1.502-.6-2.662-.49-1.046.1-1.953.445-2.692 1.023-.818.64-1.416 1.546-1.78 2.693-.374 1.18-.563 2.575-.563 4.148 0 1.472.169 2.787.502 3.911.322 1.087.797 1.975 1.413 2.64.6.646 1.298 1.051 2.073 1.2.52.1 1.08.105 1.666.014l.246 1.986c-.79.123-1.578.132-2.345-.003-1.155-.19-2.16-.765-2.986-1.71-.85-.973-1.487-2.141-1.894-3.476-.42-1.373-.632-2.921-.632-4.603 0-1.845.233-3.5.692-4.925.475-1.469 1.256-2.657 2.324-3.534 1.097-.9 2.391-1.404 3.845-1.5 1.73-.114 3.148.22 4.213.996.9.654 1.49 1.57 1.757 2.72.356-.117.778-.178 1.253-.178.966 0 1.77.298 2.387.886.588.56.973 1.318 1.145 2.253.076.416.116.907.118 1.462l.002.403c0 .437-.003.867-.01 1.284.98.183 1.792.64 2.415 1.364.749.87 1.126 1.992 1.122 3.338-.062 2.62-1.318 4.676-3.735 6.108-2.01 1.193-4.326 1.798-6.875 1.797z"/>
    </svg>
  ),
  bluesky: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
      <path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.815 2.736 3.713 3.66 6.383 3.364.136-.02.275-.039.415-.056-.138.022-.276.04-.415.056-3.912.58-7.387 2.005-2.83 7.078 5.013 5.19 6.87-1.113 7.823-4.308.953 3.195 2.05 9.271 7.733 4.308 4.267-4.308 1.172-6.498-2.74-7.078a8.741 8.741 0 01-.415-.056c.14.017.279.036.415.056 2.67.297 5.568-.628 6.383-3.364.246-.828.624-5.79.624-6.478 0-.69-.139-1.861-.902-2.206-.659-.298-1.664-.62-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8z"/>
    </svg>
  ),
  google_business: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
      <path d="M12 11.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5zm0 4c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5zm0-13c-5.52 0-10 4.48-10 10s4.48 10 10 10 10-4.48 10-10-4.48-10-10-10zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"/>
    </svg>
  ),
};

// Platform colors
const PLATFORM_COLORS = {
  instagram: "from-purple-500 to-pink-500",
  facebook: "bg-blue-600",
  linkedin: "bg-blue-700",
  twitter: "bg-sky-500",
  tiktok: "bg-black",
  youtube: "bg-red-600",
  pinterest: "bg-red-700",
  threads: "bg-black",
  bluesky: "bg-sky-400",
  google_business: "bg-blue-500",
};

// Status badge colors
const STATUS_COLORS = {
  draft: "bg-slate-500",
  scheduled: "bg-blue-500",
  published: "bg-green-500",
  failed: "bg-red-500",
  cancelled: "bg-slate-400",
};

const STATUS_LABELS = {
  draft: "Draft",
  scheduled: "Scheduled",
  published: "Published",
  failed: "Failed",
  cancelled: "Cancelled",
};

const PLATFORM_LABELS = {
  instagram: "Instagram",
  facebook: "Facebook",
  linkedin: "LinkedIn",
  twitter: "X",
  tiktok: "TikTok",
  youtube: "YouTube",
  pinterest: "Pinterest",
  threads: "Threads",
  bluesky: "Bluesky",
  google_business: "Google Business",
};

const getPlatformLabel = (platform) =>
  PLATFORM_LABELS[platform] || platform.replace(/_/g, " ");

const getAccountDisplayName = (account) =>
  account?.profileName || getPlatformLabel(account?.platform || "");

const PUBLISH_MODES = {
  NOW: "now",
  SCHEDULE: "schedule",
  DRAFT: "draft",
};

const toDateTimeLocalValue = (value) => {
  const date = value ? new Date(value) : new Date(Date.now() + 5 * 60 * 1000);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
};

const formatDateKey = (dateKey) => {
  if (!dateKey || typeof dateKey !== "string") return "";
  const [year, month, day] = dateKey.split("-").map(Number);
  if (!year || !month || !day) return dateKey;
  return new Date(year, month - 1, day).toLocaleDateString();
};

const getPostTimestampLabel = (post, calendarDateKey = null) => {
  if (post?.status === "published" && post?.publishedAt) {
    const publishedDate = new Date(post.publishedAt);
    return Number.isNaN(publishedDate.getTime())
      ? "Published"
      : `Published ${publishedDate.toLocaleString()}`;
  }

  if (post?.scheduledTime) {
    const scheduledDate = new Date(post.scheduledTime);
    if (!Number.isNaN(scheduledDate.getTime())) {
      const prefix = post?.status === "scheduled" ? "Scheduled" : "Saved";
      return `${prefix} ${scheduledDate.toLocaleString()}`;
    }
  }

  if (calendarDateKey && post?.time) {
    const prefix = post?.status === "scheduled" ? "Scheduled" : "Saved";
    return `${prefix} ${formatDateKey(calendarDateKey)} at ${post.time}`;
  }

  return post?.status === "scheduled" ? "Scheduled" : "Saved";
};

// ============================================================================
// Onboarding Component - For first-time users without a brand
// ============================================================================
const OnboardingScreen = ({ onAssignBrand, isLoading }) => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="max-w-lg text-center p-8">
      <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
        <Rocket className="w-10 h-10 text-white" />
      </div>
      <h2 className="text-2xl font-bold dark:text-white text-slate-900 mb-3">
        Welcome to Social Scheduler
      </h2>
      <p className="text-slate-500 dark:text-slate-400 mb-6">
        Schedule and manage your social media posts across Instagram, Facebook, LinkedIn, Twitter, TikTok, and more - all from one place.
      </p>
      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className="p-4 rounded-xl dark:bg-slate-800/50 bg-slate-100 border dark:border-slate-700 border-slate-200">
          <Calendar className="w-6 h-6 mx-auto mb-2 text-blue-500" />
          <p className="text-sm dark:text-slate-300 text-slate-600">Calendar View</p>
        </div>
        <div className="p-4 rounded-xl dark:bg-slate-800/50 bg-slate-100 border dark:border-slate-700 border-slate-200">
          <BarChart3 className="w-6 h-6 mx-auto mb-2 text-green-500" />
          <p className="text-sm dark:text-slate-300 text-slate-600">Analytics</p>
        </div>
        <div className="p-4 rounded-xl dark:bg-slate-800/50 bg-slate-100 border dark:border-slate-700 border-slate-200">
          <Users className="w-6 h-6 mx-auto mb-2 text-purple-500" />
          <p className="text-sm dark:text-slate-300 text-slate-600">Multi-Platform</p>
        </div>
      </div>
      <button
        onClick={onAssignBrand}
        disabled={isLoading}
        className="w-full py-3 px-6 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold hover:from-purple-700 hover:to-pink-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {isLoading ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Setting up your account...
          </>
        ) : (
          <>
            <Sparkles className="w-5 h-5" />
            Get Started
          </>
        )}
      </button>
    </div>
  </div>
);

// ============================================================================
// Connect Accounts Component - User connects their own social accounts
// ============================================================================
const ConnectAccountsScreen = ({ brand, connectedAccounts, onRefresh, isLoading }) => {
  const hasAccounts = connectedAccounts && connectedAccounts.length > 0;
  const connectedCount = connectedAccounts?.length || 0;

  // Open Metricool to connect social accounts
  const handleConnectAccounts = () => {
    if (brand?.metricoolBlogId) {
      // Open Metricool integrations page for this brand in new tab
      const url = `https://app.metricool.com/${brand.metricoolBlogId}/settings/integrations`;
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="max-w-4xl w-full p-8">
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
          {hasAccounts ? (
            <CheckCircle className="w-10 h-10 text-white" />
          ) : (
            <Link2 className="w-10 h-10 text-white" />
          )}
        </div>

        {hasAccounts ? (
          <>
            <h2 className="text-3xl font-bold dark:text-white text-slate-900 mb-3 text-center">
              Your Connected Accounts
            </h2>
            <p className="text-slate-500 dark:text-slate-400 mb-6 text-center max-w-2xl mx-auto">
              Each connected account maps directly to one publishing destination. Review your channels below, then use New Post to publish or schedule content.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="p-4 rounded-xl dark:bg-slate-800/50 bg-slate-100 border dark:border-slate-700 border-slate-200">
                <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">Connected Accounts</p>
                <p className="text-3xl font-bold dark:text-white text-slate-900">{connectedCount}</p>
                <p className="text-sm text-slate-500 mt-1">Ready to schedule</p>
              </div>
              <div className="p-4 rounded-xl dark:bg-slate-800/50 bg-slate-100 border dark:border-slate-700 border-slate-200">
                <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">Workflow</p>
                <p className="text-lg font-semibold dark:text-white text-slate-900">1. Connect</p>
                <p className="text-sm text-slate-500 mt-1">2. Create Post  3. Manage Queue</p>
              </div>
              <div className="p-4 rounded-xl dark:bg-slate-800/50 bg-slate-100 border dark:border-slate-700 border-slate-200">
                <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">Metricool Brand</p>
                <p className="text-lg font-semibold dark:text-white text-slate-900">{brand?.brandId || "Assigned"}</p>
                <p className="text-sm text-slate-500 mt-1">Blog ID {brand?.metricoolBlogId || "—"}</p>
              </div>
            </div>

            <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-3">
              {connectedAccounts.map((account, i) => {
                const IconComponent = PLATFORM_ICONS[account.platform] || Link2;
                return (
                  <div
                    key={i}
                    className="flex items-center gap-3 p-4 rounded-xl dark:bg-slate-800/50 bg-slate-100 border dark:border-slate-700 border-slate-200"
                  >
                    <div className="w-11 h-11 rounded-xl dark:bg-slate-700 bg-white border dark:border-slate-600 border-slate-300 flex items-center justify-center">
                      <IconComponent className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <p className="text-sm font-semibold dark:text-white text-slate-900 truncate">
                        {getAccountDisplayName(account)}
                      </p>
                      <p className="text-xs text-slate-500">
                        {getPlatformLabel(account.platform)}
                      </p>
                    </div>
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  </div>
                );
              })}
            </div>

            <div className="mb-6 p-4 rounded-xl dark:bg-blue-500/10 bg-blue-50 border border-blue-500/20">
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-blue-500 mt-0.5" />
                <div className="text-left">
                  <p className="text-sm font-semibold dark:text-white text-slate-900">Simple publishing flow</p>
                  <p className="text-sm text-slate-500 mt-1">
                    Pick one or more connected accounts in New Post, schedule the time, and manage everything from the calendar or posts list.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3 justify-center">
              <button
                onClick={handleConnectAccounts}
                className="px-6 py-3 dark:bg-slate-800 bg-slate-200 dark:text-white text-slate-800 rounded-xl hover:dark:bg-slate-700 hover:bg-slate-300 transition-all flex items-center gap-2"
              >
                <ExternalLink className="w-5 h-5" />
                Manage Accounts
              </button>
              <button
                onClick={onRefresh}
                disabled={isLoading}
                className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all disabled:opacity-50 flex items-center gap-2"
              >
                <RefreshCw className={`w-5 h-5 ${isLoading ? "animate-spin" : ""}`} />
                Refresh
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="text-3xl font-bold dark:text-white text-slate-900 mb-3 text-center">
              Connect Your Social Accounts
            </h2>
            <p className="text-slate-500 dark:text-slate-400 mb-6 text-center max-w-2xl mx-auto">
              Keep it simple: each social account you connect becomes one publishing destination in Social Scheduler.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="p-4 rounded-xl dark:bg-slate-800/30 bg-slate-50 border dark:border-slate-700 border-slate-200">
                <p className="text-sm font-semibold dark:text-white text-slate-900 mb-1">1. Connect accounts</p>
                <p className="text-sm text-slate-500">Open Metricool and link the exact channels you want to publish to.</p>
              </div>
              <div className="p-4 rounded-xl dark:bg-slate-800/30 bg-slate-50 border dark:border-slate-700 border-slate-200">
                <p className="text-sm font-semibold dark:text-white text-slate-900 mb-1">2. Refresh here</p>
                <p className="text-sm text-slate-500">Sync the newly connected channels back into your dashboard.</p>
              </div>
              <div className="p-4 rounded-xl dark:bg-slate-800/30 bg-slate-50 border dark:border-slate-700 border-slate-200">
                <p className="text-sm font-semibold dark:text-white text-slate-900 mb-1">3. Start posting</p>
                <p className="text-sm text-slate-500">Create and manage content from one calendar and one post queue.</p>
              </div>
            </div>

            <div className="mb-6 p-4 rounded-xl dark:bg-slate-800/30 bg-slate-50 border dark:border-slate-700 border-slate-200">
              <div className="flex flex-wrap justify-center gap-3 mb-4">
                {[Instagram, Facebook, Linkedin, Twitter, Youtube].map((Icon, i) => (
                  <div key={i} className="w-10 h-10 rounded-lg dark:bg-slate-700 bg-slate-200 flex items-center justify-center">
                    <Icon className="w-5 h-5 dark:text-slate-400 text-slate-500" />
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500">
                Supported: Instagram, Facebook, LinkedIn, X, TikTok, YouTube, Threads, Pinterest
              </p>
            </div>

            <div className="space-y-3">
              <button
                onClick={handleConnectAccounts}
                className="w-full py-3 px-6 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-cyan-700 transition-all flex items-center justify-center gap-2"
              >
                <ExternalLink className="w-5 h-5" />
                Connect Social Accounts
              </button>

              <p className="text-xs text-slate-500">
                Opens in a new tab. After connecting, click Refresh below.
              </p>

              <button
                onClick={onRefresh}
                disabled={isLoading}
                className="w-full py-3 px-6 dark:bg-slate-800 bg-slate-200 dark:text-white text-slate-800 rounded-xl hover:dark:bg-slate-700 hover:bg-slate-300 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <RefreshCw className={`w-5 h-5 ${isLoading ? "animate-spin" : ""}`} />
                Refresh
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Post Composer Modal
// ============================================================================
const PostComposerModal = ({ isOpen, onClose, config, onSubmit, initialData = null }) => {
  const [content, setContent] = useState(initialData?.content || "");
  const [selectedPlatforms, setSelectedPlatforms] = useState(initialData?.platforms || []);
  const [scheduledDate, setScheduledDate] = useState(
    toDateTimeLocalValue(initialData?.scheduledTime)
  );
  const [timezone, setTimezone] = useState(initialData?.timezone || "America/Chicago");
  const [mediaUrls, setMediaUrls] = useState(initialData?.mediaUrls || []);
  const [newMediaUrl, setNewMediaUrl] = useState("");
  const [firstComment, setFirstComment] = useState(initialData?.firstComment || "");
  const [publishMode, setPublishMode] = useState(() => {
    if (initialData?.status === "draft") return PUBLISH_MODES.DRAFT;
    if (initialData?.scheduledTime) return PUBLISH_MODES.SCHEDULE;
    return PUBLISH_MODES.NOW;
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const availableAccounts = config?.connectedAccounts || [];
  const isDraft = publishMode === PUBLISH_MODES.DRAFT;
  const isScheduleMode = publishMode === PUBLISH_MODES.SCHEDULE;
  const isPostNow = publishMode === PUBLISH_MODES.NOW;

  // Calculate estimated credits
  const calculateCredits = () => {
    if (!config?.credits) return 0;
    const baseCost = config.credits.basePost || 50;
    const extraPlatformCost = config.credits.extraPlatform || 25;
    const platformCount = selectedPlatforms.length;
    if (platformCount === 0) return 0;
    return baseCost + Math.max(0, platformCount - 1) * extraPlatformCost;
  };

  const togglePlatform = (platform) => {
    setSelectedPlatforms((prev) =>
      prev.includes(platform) ? prev.filter((p) => p !== platform) : [...prev, platform]
    );
  };

  const addMediaUrl = () => {
    if (newMediaUrl.trim() && mediaUrls.length < 10) {
      setMediaUrls([...mediaUrls, newMediaUrl.trim()]);
      setNewMediaUrl("");
    }
  };

  const removeMediaUrl = (index) => {
    setMediaUrls(mediaUrls.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    setError(null);

    if (!content.trim()) {
      setError("Post content is required");
      return;
    }
    if (selectedPlatforms.length === 0) {
      setError("Select at least one platform");
      return;
    }
    if (isScheduleMode && !scheduledDate) {
      setError("Scheduled time is required");
      return;
    }

    const scheduledTime = isScheduleMode
      ? new Date(scheduledDate).toISOString()
      : new Date(Date.now() + 2 * 60 * 1000).toISOString();

    setIsSubmitting(true);
    try {
      await onSubmit({
        content: content.trim(),
        platforms: selectedPlatforms,
        scheduledTime,
        timezone,
        mediaUrls: mediaUrls.length > 0 ? mediaUrls : undefined,
        firstComment: firstComment.trim() || undefined,
        isDraft,
      });
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white dark:bg-slate-900 rounded-2xl shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between p-4 border-b dark:border-slate-700 border-slate-200 bg-white dark:bg-slate-900">
          <h2 className="text-xl font-bold dark:text-white text-slate-900">
            {initialData ? "Edit Post" : "Create New Post"}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg dark:hover:bg-slate-800 hover:bg-slate-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Error message */}
          {error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-500 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Content */}
          <div>
            <label className="block text-sm font-medium dark:text-slate-300 text-slate-700 mb-2">
              Post Content
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="What's on your mind?"
              rows={4}
              maxLength={2200}
              className="w-full px-4 py-3 rounded-xl dark:bg-slate-800 bg-slate-100 dark:text-white text-slate-900 border dark:border-slate-700 border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
            <p className="mt-1 text-xs text-slate-500">{content.length}/2200 characters</p>
          </div>

          {/* Platform Selection */}
          <div>
            <label className="block text-sm font-medium dark:text-slate-300 text-slate-700 mb-2">
              Connected Accounts
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {availableAccounts.length > 0 ? (
                availableAccounts.map((account) => {
                  const platform = account.platform;
                  const IconComponent = PLATFORM_ICONS[platform] || Link2;
                  const isSelected = selectedPlatforms.includes(platform);
                  return (
                    <button
                      key={account.id || platform}
                      onClick={() => togglePlatform(platform)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-all ${
                        isSelected
                          ? "bg-blue-600 border-blue-600 text-white"
                          : "dark:bg-slate-800 bg-slate-100 dark:border-slate-700 border-slate-300 dark:text-slate-300 text-slate-700 hover:border-blue-500"
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isSelected ? "bg-white/15" : "dark:bg-slate-700 bg-white"}`}>
                        <IconComponent className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold truncate">
                          {getAccountDisplayName(account)}
                        </p>
                        <p className={`text-xs ${isSelected ? "text-blue-100" : "text-slate-500"}`}>
                          {getPlatformLabel(platform)}
                        </p>
                      </div>
                    </button>
                  );
                })
              ) : (
                <p className="text-sm text-slate-500">No connected accounts. Connect your accounts first.</p>
              )}
            </div>
          </div>

          {/* Schedule */}
          <div>
            <label className="block text-sm font-medium dark:text-slate-300 text-slate-700 mb-2">
              Publish Mode
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                {
                  id: PUBLISH_MODES.NOW,
                  label: "Post Now",
                  description: "Queue this post immediately in Metricool.",
                },
                {
                  id: PUBLISH_MODES.SCHEDULE,
                  label: "Schedule",
                  description: "Pick the exact date and time to publish.",
                },
                {
                  id: PUBLISH_MODES.DRAFT,
                  label: "Save Draft",
                  description: "Keep it private until you are ready.",
                },
              ].map((mode) => {
                const isSelected = publishMode === mode.id;
                return (
                  <button
                    key={mode.id}
                    onClick={() => setPublishMode(mode.id)}
                    className={`p-4 rounded-xl border text-left transition-all ${
                      isSelected
                        ? "bg-blue-600 border-blue-600 text-white"
                        : "dark:bg-slate-800 bg-slate-100 dark:border-slate-700 border-slate-300 dark:text-slate-300 text-slate-700 hover:border-blue-500"
                    }`}
                  >
                    <p className="text-sm font-semibold">{mode.label}</p>
                    <p className={`mt-1 text-xs ${isSelected ? "text-blue-100" : "text-slate-500"}`}>
                      {mode.description}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium dark:text-slate-300 text-slate-700 mb-2">
                {isPostNow ? "Will Publish" : "Schedule For"}
              </label>
              <input
                type="datetime-local"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                disabled={!isScheduleMode}
                className="w-full px-4 py-3 rounded-xl dark:bg-slate-800 bg-slate-100 dark:text-white text-slate-900 border dark:border-slate-700 border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
              />
              <p className="mt-1 text-xs text-slate-500">
                {isPostNow
                  ? "This sends the post to Metricool with a short safety buffer so the scheduler can process it reliably."
                  : isDraft
                    ? "Drafts do not publish anywhere until you reopen and post them."
                    : "Choose when this post should go live."}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium dark:text-slate-300 text-slate-700 mb-2">
                Timezone
              </label>
              <select
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                className="w-full px-4 py-3 rounded-xl dark:bg-slate-800 bg-slate-100 dark:text-white text-slate-900 border dark:border-slate-700 border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="America/Chicago">Central (Chicago)</option>
                <option value="America/New_York">Eastern (New York)</option>
                <option value="America/Denver">Mountain (Denver)</option>
                <option value="America/Los_Angeles">Pacific (Los Angeles)</option>
                <option value="UTC">UTC</option>
              </select>
            </div>
          </div>

          {/* Media URLs */}
          <div>
            <label className="block text-sm font-medium dark:text-slate-300 text-slate-700 mb-2">
              Media URLs (Optional)
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="url"
                value={newMediaUrl}
                onChange={(e) => setNewMediaUrl(e.target.value)}
                placeholder="https://example.com/image.jpg"
                className="flex-1 px-4 py-2 rounded-xl dark:bg-slate-800 bg-slate-100 dark:text-white text-slate-900 border dark:border-slate-700 border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                onClick={addMediaUrl}
                disabled={mediaUrls.length >= 10}
                className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>
            {mediaUrls.length > 0 && (
              <div className="space-y-2">
                {mediaUrls.map((url, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 p-2 rounded-lg dark:bg-slate-800/50 bg-slate-100"
                  >
                    <ImageIcon className="w-4 h-4 text-slate-500" />
                    <span className="flex-1 text-sm truncate dark:text-slate-300 text-slate-600">
                      {url}
                    </span>
                    <button onClick={() => removeMediaUrl(i)} className="text-red-500 hover:text-red-400">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* First Comment (Instagram) */}
          {selectedPlatforms.includes("instagram") && (
            <div>
              <label className="block text-sm font-medium dark:text-slate-300 text-slate-700 mb-2">
                First Comment (Instagram)
              </label>
              <textarea
                value={firstComment}
                onChange={(e) => setFirstComment(e.target.value)}
                placeholder="Add hashtags or additional text as a first comment..."
                rows={2}
                maxLength={2200}
                className="w-full px-4 py-3 rounded-xl dark:bg-slate-800 bg-slate-100 dark:text-white text-slate-900 border dark:border-slate-700 border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </div>
          )}

          {/* Credits cost */}
          <div className="p-4 rounded-xl dark:bg-slate-800/50 bg-slate-100 border dark:border-slate-700 border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              <span className="text-sm dark:text-slate-300 text-slate-700">Estimated Cost:</span>
            </div>
            <span className="text-lg font-bold dark:text-white text-slate-900">
              {isDraft ? "Free" : `${calculateCredits()} credits`}
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 flex items-center justify-end gap-3 p-4 border-t dark:border-slate-700 border-slate-200 bg-white dark:bg-slate-900">
          <button
            onClick={onClose}
            className="px-6 py-2.5 rounded-xl dark:bg-slate-800 bg-slate-200 dark:text-white text-slate-800 hover:dark:bg-slate-700 hover:bg-slate-300 transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {isDraft ? "Saving..." : isPostNow ? "Posting..." : "Scheduling..."}
              </>
            ) : (
              <>
                {isDraft ? <Edit3 className="w-5 h-5" /> : <Send className="w-5 h-5" />}
                {isDraft ? "Save Draft" : isPostNow ? "Post Now" : "Schedule Post"}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Calendar View Component
// ============================================================================
const CalendarView = ({ posts, currentMonth, onMonthChange, onSelectDate }) => {
  const [year, month] = currentMonth.split("-").map(Number);

  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDayOfMonth = new Date(year, month - 1, 1).getDay();
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const paddingDays = Array.from({ length: firstDayOfMonth }, (_, i) => null);

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];

  const prevMonth = () => {
    const prevDate = new Date(year, month - 2, 1);
    onMonthChange(`${prevDate.getFullYear()}-${String(prevDate.getMonth() + 1).padStart(2, "0")}`);
  };

  const nextMonth = () => {
    const nextDate = new Date(year, month, 1);
    onMonthChange(`${nextDate.getFullYear()}-${String(nextDate.getMonth() + 1).padStart(2, "0")}`);
  };

  const getPostsForDay = (day) => {
    const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return posts?.posts?.[dateStr] || [];
  };

  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl border dark:border-slate-700 border-slate-200 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b dark:border-slate-700 border-slate-200">
        <button
          onClick={prevMonth}
          className="p-2 rounded-lg dark:hover:bg-slate-800 hover:bg-slate-100"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h3 className="text-lg font-semibold dark:text-white text-slate-900">
          {monthNames[month - 1]} {year}
        </h3>
        <button
          onClick={nextMonth}
          className="p-2 rounded-lg dark:hover:bg-slate-800 hover:bg-slate-100"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 border-b dark:border-slate-700 border-slate-200">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
          <div
            key={day}
            className="p-3 text-center text-sm font-medium dark:text-slate-400 text-slate-500"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7">
        {/* Padding days */}
        {paddingDays.map((_, i) => (
          <div
            key={`pad-${i}`}
            className="aspect-square p-2 border-b border-r dark:border-slate-800 border-slate-100"
          />
        ))}

        {/* Actual days */}
        {days.map((day) => {
          const dayPosts = getPostsForDay(day);
          const isToday =
            new Date().getFullYear() === year &&
            new Date().getMonth() + 1 === month &&
            new Date().getDate() === day;

          return (
            <div
              key={day}
              onClick={() => onSelectDate(`${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`)}
              className={`aspect-square p-2 border-b border-r dark:border-slate-800 border-slate-100 cursor-pointer hover:dark:bg-slate-800/50 hover:bg-slate-50 transition-colors ${
                isToday ? "dark:bg-blue-900/20 bg-blue-50" : ""
              }`}
            >
              <div
                className={`text-sm font-medium mb-1 ${
                  isToday
                    ? "w-7 h-7 flex items-center justify-center rounded-full bg-blue-600 text-white"
                    : "dark:text-slate-300 text-slate-700"
                }`}
              >
                {day}
              </div>
              {dayPosts.length > 0 && (
                <div className="space-y-1">
                  {dayPosts.slice(0, 3).map((post, i) => (
                    <div
                      key={i}
                      className={`h-1.5 rounded-full ${STATUS_COLORS[post.status] || "bg-slate-500"}`}
                    />
                  ))}
                  {dayPosts.length > 3 && (
                    <span className="text-xs text-slate-500">+{dayPosts.length - 3}</span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============================================================================
// Posts List Component
// ============================================================================
const PostsList = ({ posts, isLoading, onEdit, onCancel, onView, calendarDateKey = null }) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="text-center py-12">
        <Calendar className="w-12 h-12 mx-auto mb-4 text-slate-400" />
        <p className="text-slate-500 dark:text-slate-400">No posts scheduled yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {posts.map((post) => (
        <div
          key={post.id}
          className="p-4 rounded-xl dark:bg-slate-800/50 bg-white border dark:border-slate-700 border-slate-200 hover:border-blue-500/50 transition-all"
        >
          <div className="flex items-start gap-4">
            {/* Platforms */}
            <div className="flex flex-col gap-1">
              {post.platforms.map((platform) => {
                const IconComponent = PLATFORM_ICONS[platform.toLowerCase()] || Link2;
                return (
                  <div
                    key={platform}
                    className="w-8 h-8 rounded-lg dark:bg-slate-700 bg-slate-100 flex items-center justify-center"
                  >
                    <IconComponent className="w-4 h-4" />
                  </div>
                );
              })}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <p className="dark:text-white text-slate-900 line-clamp-2">{post.content}</p>
              <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {getPostTimestampLabel(post, calendarDateKey)}
                </span>
                <span
                  className={`px-2 py-0.5 rounded-full text-xs text-white ${STATUS_COLORS[post.status]}`}
                >
                  {STATUS_LABELS[post.status]}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => onView(post)}
                className="p-2 rounded-lg dark:hover:bg-slate-700 hover:bg-slate-100"
                title="View"
              >
                <Eye className="w-4 h-4" />
              </button>
              {["draft", "scheduled"].includes(post.status) && (
                <>
                  <button
                    onClick={() => onEdit(post)}
                    className="p-2 rounded-lg dark:hover:bg-slate-700 hover:bg-slate-100"
                    title="Edit"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onCancel(post.id)}
                    className="p-2 rounded-lg text-red-500 dark:hover:bg-red-500/10 hover:bg-red-50"
                    title="Cancel"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// Analytics Dashboard Component
// ============================================================================
const AnalyticsDashboard = ({ config, posts }) => {
  const [analytics, setAnalytics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [platformAnalytics, setPlatformAnalytics] = useState(null);
  const [dateRange, setDateRange] = useState("30d");

  // Calculate date range
  const getDateRange = () => {
    const end = new Date();
    const start = new Date();
    switch (dateRange) {
      case "7d":
        start.setDate(end.getDate() - 7);
        break;
      case "30d":
        start.setDate(end.getDate() - 30);
        break;
      case "90d":
        start.setDate(end.getDate() - 90);
        break;
      default:
        start.setDate(end.getDate() - 30);
    }
    return {
      startDate: start.toISOString().split("T")[0],
      endDate: end.toISOString().split("T")[0],
    };
  };

  // Load analytics summary
  useEffect(() => {
    const loadAnalytics = async () => {
      setIsLoading(true);
      try {
        const data = await socialSchedulerService.getAnalyticsSummary();
        setAnalytics(data);
      } catch (err) {
        console.error("Failed to load analytics:", err);
      } finally {
        setIsLoading(false);
      }
    };
    loadAnalytics();
  }, [dateRange]);

  // Load platform-specific analytics
  useEffect(() => {
    if (!selectedPlatform) {
      setPlatformAnalytics(null);
      return;
    }
    const loadPlatformAnalytics = async () => {
      try {
        const { startDate, endDate } = getDateRange();
        const data = await socialSchedulerService.getPlatformAnalytics(
          selectedPlatform,
          startDate,
          endDate
        );
        setPlatformAnalytics(data);
      } catch (err) {
        console.error("Failed to load platform analytics:", err);
      }
    };
    loadPlatformAnalytics();
  }, [selectedPlatform, dateRange]);

  // Calculate post stats
  const postStats = useMemo(() => {
    const stats = {
      total: posts?.length || 0,
      scheduled: 0,
      published: 0,
      failed: 0,
      draft: 0,
      byPlatform: {},
    };

    posts?.forEach((post) => {
      if (post.status === "scheduled") stats.scheduled++;
      else if (post.status === "published") stats.published++;
      else if (post.status === "failed") stats.failed++;
      else if (post.status === "draft") stats.draft++;

      post.platforms?.forEach((p) => {
        stats.byPlatform[p] = (stats.byPlatform[p] || 0) + 1;
      });
    });

    return stats;
  }, [posts]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Date Range Selector */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold dark:text-white text-slate-900">
          Analytics Overview
        </h3>
        <div className="flex gap-1 p-1 rounded-lg dark:bg-slate-800 bg-slate-200">
          {[
            { value: "7d", label: "7 Days" },
            { value: "30d", label: "30 Days" },
            { value: "90d", label: "90 Days" },
          ].map((opt) => (
            <button
              key={opt.value}
              onClick={() => setDateRange(opt.value)}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-all ${
                dateRange === opt.value
                  ? "bg-white dark:bg-slate-700 shadow dark:text-white text-slate-900"
                  : "dark:text-slate-400 text-slate-600"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 rounded-xl dark:bg-slate-800/50 bg-white border dark:border-slate-700 border-slate-200">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <Send className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold dark:text-white text-slate-900">
                {postStats.total}
              </p>
              <p className="text-sm text-slate-500">Total Posts</p>
            </div>
          </div>
        </div>
        <div className="p-4 rounded-xl dark:bg-slate-800/50 bg-white border dark:border-slate-700 border-slate-200">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold dark:text-white text-slate-900">
                {postStats.published}
              </p>
              <p className="text-sm text-slate-500">Published</p>
            </div>
          </div>
        </div>
        <div className="p-4 rounded-xl dark:bg-slate-800/50 bg-white border dark:border-slate-700 border-slate-200">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <p className="text-2xl font-bold dark:text-white text-slate-900">
                {postStats.scheduled}
              </p>
              <p className="text-sm text-slate-500">Scheduled</p>
            </div>
          </div>
        </div>
        <div className="p-4 rounded-xl dark:bg-slate-800/50 bg-white border dark:border-slate-700 border-slate-200">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
              <XCircle className="w-5 h-5 text-red-500" />
            </div>
            <div>
              <p className="text-2xl font-bold dark:text-white text-slate-900">
                {postStats.failed}
              </p>
              <p className="text-sm text-slate-500">Failed</p>
            </div>
          </div>
        </div>
      </div>

      {/* Platform Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Posts by Platform */}
        <div className="p-6 rounded-xl dark:bg-slate-800/50 bg-white border dark:border-slate-700 border-slate-200">
          <h4 className="text-md font-semibold dark:text-white text-slate-900 mb-4">
            Posts by Platform
          </h4>
          <div className="space-y-3">
            {config?.connectedAccounts?.map((account) => {
              const IconComponent = PLATFORM_ICONS[account.platform] || Link2;
              const count = postStats.byPlatform[account.platform] || 0;
              const percentage = postStats.total > 0 ? (count / postStats.total) * 100 : 0;

              return (
                <div
                  key={account.platform}
                  onClick={() => setSelectedPlatform(account.platform)}
                  className={`p-3 rounded-lg cursor-pointer transition-all ${
                    selectedPlatform === account.platform
                      ? "dark:bg-blue-500/20 bg-blue-50 border border-blue-500"
                      : "dark:bg-slate-700/50 bg-slate-50 hover:dark:bg-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <IconComponent className="w-4 h-4" />
                      <span className="text-sm font-medium dark:text-white text-slate-900 capitalize">
                        {getPlatformLabel(account.platform)}
                      </span>
                    </div>
                    <span className="text-sm dark:text-slate-300 text-slate-600">
                      {count} posts
                    </span>
                  </div>
                  <div className="h-2 rounded-full dark:bg-slate-600 bg-slate-200 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Platform Details */}
        <div className="p-6 rounded-xl dark:bg-slate-800/50 bg-white border dark:border-slate-700 border-slate-200">
          <h4 className="text-md font-semibold dark:text-white text-slate-900 mb-4">
            {selectedPlatform ? (
              <span className="capitalize">{selectedPlatform.replace("_", " ")} Analytics</span>
            ) : (
              "Select a Platform"
            )}
          </h4>

          {selectedPlatform && analytics?.platforms?.[selectedPlatform] ? (
            <div className="space-y-4">
              {analytics.platforms[selectedPlatform].error ? (
                <div className="p-4 rounded-lg bg-amber-500/10 text-amber-500 text-sm">
                  Analytics data not available for this platform yet.
                  Keep posting to see insights!
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(analytics.platforms[selectedPlatform].metrics || {}).slice(0, 6).map(([key, value]) => (
                      <div key={key} className="p-3 rounded-lg dark:bg-slate-700/50 bg-slate-100">
                        <p className="text-lg font-bold dark:text-white text-slate-900">
                          {typeof value === "number" ? value.toLocaleString() : value}
                        </p>
                        <p className="text-xs text-slate-500 capitalize">
                          {key.replace(/_/g, " ")}
                        </p>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8">
              <BarChart3 className="w-12 h-12 text-slate-400 mb-3" />
              <p className="text-sm text-slate-500 text-center">
                Click on a platform to view detailed analytics
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Best Times to Post */}
      <div className="p-6 rounded-xl dark:bg-slate-800/50 bg-white border dark:border-slate-700 border-slate-200">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-md font-semibold dark:text-white text-slate-900">
            Best Times to Post
          </h4>
          <span className="text-xs px-2 py-1 rounded-full dark:bg-amber-500/20 bg-amber-100 text-amber-600">
            <Sparkles className="w-3 h-3 inline mr-1" />
            25 credits per analysis
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
            <div
              key={day}
              className="p-3 rounded-lg dark:bg-slate-700/50 bg-slate-100 text-center"
            >
              <p className="text-sm font-medium dark:text-white text-slate-900 mb-2">
                {day}
              </p>
              <div className="space-y-1">
                {[9, 12, 18].map((hour) => (
                  <div
                    key={hour}
                    className="text-xs px-2 py-1 rounded dark:bg-slate-600 bg-slate-200 dark:text-slate-300 text-slate-600"
                  >
                    {hour}:00
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <p className="mt-3 text-xs text-slate-500 text-center">
          Times shown are optimal posting hours based on typical engagement patterns
        </p>
      </div>
    </div>
  );
};

// ============================================================================
// Admin Panel Component - For viewing and managing all brands
// ============================================================================
const AdminPanel = ({ onSelectUser }) => {
  const [brands, setBrands] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [brandDetails, setBrandDetails] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [searchEmail, setSearchEmail] = useState("");
  const [searchResult, setSearchResult] = useState(null);

  // Load brand stats and list
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const [statsData, brandsData] = await Promise.all([
          socialSchedulerService.getBrandStats(),
          socialSchedulerService.listAllBrands(statusFilter),
        ]);
        setStats(statsData);
        setBrands(brandsData.brands || []);
      } catch (err) {
        console.error("Failed to load admin data:", err);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [statusFilter]);

  // Load brand details when selected
  useEffect(() => {
    if (!selectedBrand) {
      setBrandDetails(null);
      return;
    }
    const loadDetails = async () => {
      try {
        const data = await socialSchedulerService.getBrandDetails(selectedBrand);
        setBrandDetails(data);
      } catch (err) {
        console.error("Failed to load brand details:", err);
      }
    };
    loadDetails();
  }, [selectedBrand]);

  // Search for user's brand
  const handleSearch = async () => {
    if (!searchEmail.trim()) return;
    try {
      const result = await socialSchedulerService.getUserBrand(searchEmail);
      setSearchResult(result);
    } catch (err) {
      console.error("Failed to search:", err);
      setSearchResult({ error: err.message });
    }
  };

  // Release a brand
  const handleRelease = async (brandId) => {
    if (!window.confirm("Are you sure you want to release this brand? The user will lose access to their social accounts.")) {
      return;
    }
    try {
      await socialSchedulerService.releaseBrand(brandId);
      // Refresh data
      const [statsData, brandsData] = await Promise.all([
        socialSchedulerService.getBrandStats(),
        socialSchedulerService.listAllBrands(statusFilter),
      ]);
      setStats(statsData);
      setBrands(brandsData.brands || []);
      setSelectedBrand(null);
    } catch (err) {
      alert(`Failed to release brand: ${err.message}`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="p-4 rounded-xl dark:bg-slate-800/50 bg-white border dark:border-slate-700 border-slate-200">
            <p className="text-2xl font-bold dark:text-white text-slate-900">{stats.total}</p>
            <p className="text-sm text-slate-500">Total Brands</p>
          </div>
          <div className="p-4 rounded-xl dark:bg-slate-800/50 bg-white border dark:border-slate-700 border-slate-200">
            <p className="text-2xl font-bold text-green-500">{stats.available}</p>
            <p className="text-sm text-slate-500">Available</p>
          </div>
          <div className="p-4 rounded-xl dark:bg-slate-800/50 bg-white border dark:border-slate-700 border-slate-200">
            <p className="text-2xl font-bold text-blue-500">{stats.assigned}</p>
            <p className="text-sm text-slate-500">Assigned</p>
          </div>
          <div className="p-4 rounded-xl dark:bg-slate-800/50 bg-white border dark:border-slate-700 border-slate-200">
            <p className="text-2xl font-bold text-amber-500">{stats.pending}</p>
            <p className="text-sm text-slate-500">Pending</p>
          </div>
          <div className="p-4 rounded-xl dark:bg-slate-800/50 bg-white border dark:border-slate-700 border-slate-200">
            <p className="text-2xl font-bold dark:text-white text-slate-900">{stats.capacityPercent}%</p>
            <p className="text-sm text-slate-500">Capacity Used</p>
          </div>
        </div>
      )}

      {/* Search User */}
      <div className="p-4 rounded-xl dark:bg-slate-800/50 bg-white border dark:border-slate-700 border-slate-200">
        <h4 className="text-md font-semibold dark:text-white text-slate-900 mb-3">
          Search User's Brand
        </h4>
        <div className="flex gap-2">
          <input
            type="email"
            value={searchEmail}
            onChange={(e) => setSearchEmail(e.target.value)}
            placeholder="user@example.com"
            className="flex-1 px-4 py-2 rounded-xl dark:bg-slate-700 bg-slate-100 dark:text-white text-slate-900 border dark:border-slate-600 border-slate-300 focus:ring-2 focus:ring-blue-500"
            onKeyPress={(e) => e.key === "Enter" && handleSearch()}
          />
          <button
            onClick={handleSearch}
            className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700"
          >
            Search
          </button>
        </div>
        {searchResult && (
          <div className="mt-3 p-3 rounded-lg dark:bg-slate-700/50 bg-slate-100">
            {searchResult.error ? (
              <p className="text-red-500 text-sm">{searchResult.error}</p>
            ) : searchResult.hasBrand ? (
              <div>
                <p className="text-sm dark:text-white text-slate-900">
                  <strong>{searchResult.user}</strong> has brand <strong>{searchResult.brand.brandId}</strong>
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Status: {searchResult.brand.status} |
                  Accounts: {searchResult.connectedAccounts?.length || 0}
                </p>
                <button
                  onClick={() => setSelectedBrand(searchResult.brand.brandId)}
                  className="mt-2 text-sm text-blue-500 hover:text-blue-400"
                >
                  View Details
                </button>
              </div>
            ) : (
              <p className="text-sm text-slate-500">
                {searchResult.user} does not have a brand assigned.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Brands List */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-md font-semibold dark:text-white text-slate-900">
              All Brands ({brands.length})
            </h4>
            <div className="flex gap-1 p-1 rounded-lg dark:bg-slate-800 bg-slate-200">
              {[
                { value: null, label: "All" },
                { value: "available", label: "Available" },
                { value: "assigned", label: "Assigned" },
                { value: "pending", label: "Pending" },
              ].map((opt) => (
                <button
                  key={opt.value || "all"}
                  onClick={() => setStatusFilter(opt.value)}
                  className={`px-3 py-1 rounded text-sm transition-all ${
                    statusFilter === opt.value
                      ? "bg-white dark:bg-slate-700 shadow dark:text-white text-slate-900"
                      : "dark:text-slate-400 text-slate-600"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {brands.map((brand) => (
              <div
                key={brand.brandId}
                onClick={() => setSelectedBrand(brand.brandId)}
                className={`p-3 rounded-xl cursor-pointer transition-all ${
                  selectedBrand === brand.brandId
                    ? "dark:bg-blue-500/20 bg-blue-50 border border-blue-500"
                    : "dark:bg-slate-800/50 bg-white border dark:border-slate-700 border-slate-200 hover:border-blue-500/50"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium dark:text-white text-slate-900">
                      {brand.brandId}
                    </p>
                    {brand.userId ? (
                      <p className="text-xs text-slate-500">{brand.userId}</p>
                    ) : (
                      <p className="text-xs text-green-500">Available</p>
                    )}
                  </div>
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs ${
                      brand.status === "available"
                        ? "bg-green-500/20 text-green-500"
                        : brand.status === "assigned"
                        ? "bg-blue-500/20 text-blue-500"
                        : brand.status === "pending"
                        ? "bg-amber-500/20 text-amber-500"
                        : "bg-slate-500/20 text-slate-500"
                    }`}
                  >
                    {brand.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Brand Details */}
        <div className="p-4 rounded-xl dark:bg-slate-800/50 bg-white border dark:border-slate-700 border-slate-200">
          <h4 className="text-md font-semibold dark:text-white text-slate-900 mb-4">
            Brand Details
          </h4>
          {brandDetails ? (
            <div className="space-y-4">
              <div>
                <p className="text-xs text-slate-500">Brand ID</p>
                <p className="text-sm font-medium dark:text-white text-slate-900">
                  {brandDetails.brand.brandId}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Metricool Blog ID</p>
                <p className="text-sm font-mono dark:text-slate-300 text-slate-600">
                  {brandDetails.brand.metricoolBlogId}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Assigned To</p>
                <p className="text-sm dark:text-white text-slate-900">
                  {brandDetails.brand.userId || "—"}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Status</p>
                <p className="text-sm dark:text-white text-slate-900 capitalize">
                  {brandDetails.brand.status}
                </p>
              </div>
              {brandDetails.accounts?.length > 0 && (
                <div>
                  <p className="text-xs text-slate-500 mb-2">Connected Accounts</p>
                  <div className="space-y-1">
                    {brandDetails.accounts.map((acc, i) => {
                      const IconComponent = PLATFORM_ICONS[acc.platform] || Link2;
                      return (
                        <div key={i} className="flex items-center gap-2 text-sm">
                          <IconComponent className="w-4 h-4" />
                          <span className="dark:text-slate-300 text-slate-600">
                            {acc.profileName || acc.platform}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {brandDetails.recentPosts?.length > 0 && (
                <div>
                  <p className="text-xs text-slate-500 mb-2">Recent Posts</p>
                  <div className="space-y-2">
                    {brandDetails.recentPosts.slice(0, 3).map((post, i) => (
                      <div key={i} className="p-2 rounded-lg dark:bg-slate-700/50 bg-slate-100">
                        <p className="text-xs dark:text-slate-300 text-slate-600 line-clamp-2">
                          {post.content}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          {post.status} • {post.platforms?.join(", ")}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {brandDetails.brand.userId && (
                <button
                  onClick={() => handleRelease(brandDetails.brand.brandId)}
                  className="w-full mt-4 px-4 py-2 bg-red-500/10 text-red-500 rounded-lg hover:bg-red-500/20 transition-colors"
                >
                  Release Brand
                </button>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <Users className="w-10 h-10 mx-auto mb-3 text-slate-400" />
              <p className="text-sm text-slate-500">
                Select a brand to view details
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Main Social Scheduler Component
// ============================================================================
const SocialScheduler = ({ userEmail, isAdmin = false }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("calendar"); // calendar | posts | accounts
  const [isComposerOpen, setIsComposerOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [calendar, setCalendar] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });
  const [selectedDate, setSelectedDate] = useState(null);

  // Load initial config
  const loadConfig = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const configData = await socialSchedulerService.getConfig();
      setConfig(configData);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  // Load calendar when month changes
  useEffect(() => {
    if (config?.userBrand) {
      const [year, month] = currentMonth.split("-").map(Number);
      socialSchedulerService.getCalendar(year, month).then(setCalendar).catch(console.error);
    }
  }, [currentMonth, config?.userBrand]);

  // Load posts list
  const loadPosts = useCallback(async () => {
    setPostsLoading(true);
    try {
      const data = await socialSchedulerService.listPosts({ limit: 50 });
      setPosts(data.posts || []);
    } catch (err) {
      console.error("Failed to load posts:", err);
    } finally {
      setPostsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (config?.userBrand) {
      loadPosts();
    }
  }, [config?.userBrand, loadPosts]);

  // Assign brand to user
  const handleAssignBrand = async () => {
    setIsLoading(true);
    try {
      await socialSchedulerService.assignBrand();
      await loadConfig();
    } catch (err) {
      setError(err.message);
      setIsLoading(false);
    }
  };

  // Note: Social accounts are managed via Metricool by admin

  // Refresh accounts
  const handleRefresh = async () => {
    setIsLoading(true);
    try {
      await socialSchedulerService.refreshAccounts();
      await loadConfig();
    } catch (err) {
      console.error("Failed to refresh:", err);
    }
    setIsLoading(false);
  };

  // Create/update post
  const handleSubmitPost = async (postData) => {
    if (editingPost) {
      await socialSchedulerService.updatePost(editingPost.id, postData);
    } else {
      await socialSchedulerService.createPost(postData);
    }
    setEditingPost(null);
    loadPosts();
    // Refresh calendar
    const [year, month] = currentMonth.split("-").map(Number);
    const calendarData = await socialSchedulerService.getCalendar(year, month);
    setCalendar(calendarData);
  };

  // Cancel post
  const handleCancelPost = async (postId) => {
    if (!window.confirm("Are you sure you want to cancel this post? Credits will be refunded.")) {
      return;
    }
    try {
      await socialSchedulerService.cancelPost(postId);
      loadPosts();
      // Refresh calendar
      const [year, month] = currentMonth.split("-").map(Number);
      const calendarData = await socialSchedulerService.getCalendar(year, month);
      setCalendar(calendarData);
    } catch (err) {
      alert(err.message);
    }
  };

  // Loading state
  if (isLoading && !config) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  // Error state
  if (error && !config) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
          <p className="text-slate-500 dark:text-slate-400 mb-4">{error}</p>
          <button
            onClick={loadConfig}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Onboarding - no brand assigned yet
  if (!config?.userBrand) {
    return <OnboardingScreen onAssignBrand={handleAssignBrand} isLoading={isLoading} />;
  }

  // Connect accounts - brand assigned but no connected accounts
  if (!config?.connectedAccounts || config.connectedAccounts.length === 0) {
    return (
      <ConnectAccountsScreen
        brand={config.userBrand}
        connectedAccounts={config.connectedAccounts}
        onRefresh={handleRefresh}
        isLoading={isLoading}
      />
    );
  }

  // Main scheduler view
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold dark:text-white text-slate-900">Social Scheduler</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Connect accounts once, then create and manage posts from one simple workspace.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="px-4 py-2.5 dark:bg-slate-800 bg-slate-200 dark:text-white text-slate-800 rounded-xl hover:dark:bg-slate-700 hover:bg-slate-300 transition-all disabled:opacity-50 flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
            Refresh Accounts
          </button>
          <button
            onClick={() => {
              setEditingPost(null);
              setIsComposerOpen(true);
            }}
            className="px-4 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            New Post
          </button>
        </div>
      </div>

      {/* Connected Accounts Summary */}
      <div className="grid grid-cols-1 xl:grid-cols-[280px_1fr] gap-4">
        <div className="p-5 rounded-xl dark:bg-slate-800/50 bg-slate-100 border dark:border-slate-700 border-slate-200">
          <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">Connected Accounts</p>
          <p className="text-3xl font-bold dark:text-white text-slate-900">{config.connectedAccounts.length}</p>
          <p className="text-sm text-slate-500 mt-2">
            One connected account equals one publishing destination.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {config.connectedAccounts.map((account, i) => {
            const IconComponent = PLATFORM_ICONS[account.platform] || Link2;
            return (
              <div
                key={i}
                className="flex items-center gap-3 p-4 rounded-xl dark:bg-slate-800/50 bg-slate-100 border dark:border-slate-700 border-slate-200"
              >
                <div className="w-11 h-11 rounded-xl dark:bg-slate-700 bg-white border dark:border-slate-600 border-slate-300 flex items-center justify-center">
                  <IconComponent className="w-5 h-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold dark:text-white text-slate-900 truncate">
                    {getAccountDisplayName(account)}
                  </p>
                  <p className="text-xs text-slate-500">
                    {getPlatformLabel(account.platform)}
                  </p>
                </div>
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              </div>
            );
          })}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-xl dark:bg-slate-800/50 bg-slate-100 border dark:border-slate-700 border-slate-200 w-fit">
        {[
          { id: "calendar", label: "Calendar", icon: Calendar },
          { id: "posts", label: "All Posts", icon: MessageSquare },
          { id: "analytics", label: "Analytics", icon: BarChart3 },
          { id: "accounts", label: "Accounts", icon: Settings },
          // Admin tab only for admin users
          ...(isAdmin ? [{ id: "admin", label: "Admin", icon: Lock }] : []),
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id
                ? "bg-white dark:bg-slate-700 shadow dark:text-white text-slate-900"
                : "dark:text-slate-400 text-slate-600 hover:dark:text-white hover:text-slate-900"
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === "calendar" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <CalendarView
              posts={calendar}
              currentMonth={currentMonth}
              onMonthChange={setCurrentMonth}
              onSelectDate={setSelectedDate}
            />
          </div>
          <div>
            <div className="sticky top-6">
              <h3 className="text-lg font-semibold dark:text-white text-slate-900 mb-4">
                {selectedDate
                  ? `Posts for ${formatDateKey(selectedDate)}`
                  : "Select a date"}
              </h3>
              {selectedDate && calendar?.posts?.[selectedDate] ? (
                <PostsList
                  posts={calendar.posts[selectedDate]}
                  calendarDateKey={selectedDate}
                  onEdit={(post) => {
                    setEditingPost(post);
                    setIsComposerOpen(true);
                  }}
                  onCancel={handleCancelPost}
                  onView={(post) => {}}
                />
              ) : (
                <div className="text-center py-8">
                  <Calendar className="w-10 h-10 mx-auto mb-3 text-slate-400" />
                  <p className="text-sm text-slate-500">No posts on this date</p>
                  <button
                    onClick={() => {
                      setEditingPost(null);
                      setIsComposerOpen(true);
                    }}
                    className="mt-3 text-sm text-blue-500 hover:text-blue-400"
                  >
                    Schedule a post
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === "posts" && (
        <PostsList
          posts={posts}
          isLoading={postsLoading}
          onEdit={(post) => {
            setEditingPost(post);
            setIsComposerOpen(true);
          }}
          onCancel={handleCancelPost}
          onView={(post) => {}}
        />
      )}

      {activeTab === "analytics" && (
        <AnalyticsDashboard
          config={config}
          posts={posts}
        />
      )}

      {activeTab === "accounts" && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold dark:text-white text-slate-900">Connected Accounts</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {config.connectedAccounts.map((account, i) => {
              const IconComponent = PLATFORM_ICONS[account.platform] || Link2;
              return (
                <div
                  key={i}
                  className="p-4 rounded-xl dark:bg-slate-800/50 bg-white border dark:border-slate-700 border-slate-200"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-xl dark:bg-slate-700 bg-slate-100 flex items-center justify-center">
                      <IconComponent className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="font-medium dark:text-white text-slate-900">
                        {getAccountDisplayName(account)}
                      </p>
                      <p className="text-sm text-slate-500 capitalize">
                        {getPlatformLabel(account.platform)}
                      </p>
                    </div>
                    <CheckCircle className="w-5 h-5 text-green-500 ml-auto" />
                  </div>
                  <p className="text-xs text-slate-500">
                    Connected {account.connectedAt ? new Date(account.connectedAt).toLocaleDateString() : ""}
                  </p>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-slate-500 text-center mt-4">
            To add more accounts, contact your administrator.
          </p>
        </div>
      )}

      {/* Admin Panel - only visible for admin users */}
      {activeTab === "admin" && isAdmin && (
        <AdminPanel />
      )}

      {/* Post Composer Modal */}
      <PostComposerModal
        isOpen={isComposerOpen}
        onClose={() => {
          setIsComposerOpen(false);
          setEditingPost(null);
        }}
        config={config}
        onSubmit={handleSubmitPost}
        initialData={editingPost}
      />
    </div>
  );
};

export default SocialScheduler;
